import React from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, CheckCircle2, FileText, Download, ExternalLink, Minus, Plus, CalendarClock, Paperclip, X, HelpCircle } from 'lucide-react';
import { publicDocumentApi } from '../../lib/api';
import { DerouleEditor } from '../ui/DerouleEditor';
import { AddressAutocompleteInput } from './AddressAutocompleteInput';

interface Choice { id: string; label: string; price_ttc: number; vat_rate: number; has_quantity: boolean; }
interface ExistingRdv { date: string; start_time: string; end_time: string; status: 'confirme' | 'en_attente' | 'refuse'; refuse_reason?: string; }
interface CustomQuestionOption { id: string; label: string; message: string; block: boolean; has_detail_field: boolean; detail_field_type?: 'text' | 'datetime'; allow_end_date?: boolean; demarche_suivante_id?: string; }
interface CustomQuestion { id: string; question: string; type: 'text' | 'select' | 'datetime' | 'deroule'; options: CustomQuestionOption[]; allow_multiple: boolean; required: boolean; help_text: string; help_image: string; allow_end_date?: boolean; }

interface ChaineInfo { position: number; total: number; has_next: boolean; redirect_token: string; }

interface DocInfo {
    // Démarches enchaînées : renseigné uniquement quand plusieurs Démarches ont été envoyées ensemble.
    chaine?: ChaineInfo | null;
    needs_contact_info: boolean;
    company_name: string;
    logo_url: string;
    theme_color: string;
    name: string;
    message_body: string;
    instructions_text: string;
    attachment_name: string;
    has_attachment: boolean;
    response_type: 'choice' | 'question' | 'both';
    choices: Choice[];
    allow_multiple_choices: boolean;
    payment_type: 'none' | 'deposit' | 'full';
    payment_amount: number;
    deposit_percent: number;
    offer_appointment: boolean;
    rdv_duration_minutes: number | null;
    location_mode: 'domicile' | 'entreprise' | 'les_deux' | null;
    existing_rdv: ExistingRdv | null;
    custom_questions: CustomQuestion[];
    allow_document_upload: boolean;
    status: 'pending' | 'responded' | 'completed';
    amount_due: number;
    response_choices: { id: string; label: string; qty: number; price_ttc: number; vat_rate: number }[];
    response_text: string;
    custom_answers: { id: string; question: string; type: 'text' | 'select'; answer: string }[];
    payment_status: 'not_required' | 'pending' | 'paid';
    stripe_payment_link: string;
    known_address: { address: string; postal_code: string; city: string } | null;
}

const formatPrice = (amount: number) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(amount) || 0);
const getSelectedLabels = (answer?: string) => (answer ? answer.split(', ').filter(Boolean) : []);

const PoweredByFooter: React.FC = () => (
    <a href="https://gestion.l-iamani.com" target="_blank" rel="noopener noreferrer"
        className="mt-5 inline-flex items-center gap-1 text-[11px] text-content-muted hover:text-content transition-colors">
        Propulsé par <span className="font-bold">L'IAmani</span>
    </a>
);

export const DocumentationResponse: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [phase, setPhase] = React.useState<'loading' | 'notfound' | 'ready'>('loading');
    const [info, setInfo] = React.useState<DocInfo | null>(null);
    const [responded, setResponded] = React.useState(false);
    const [selections, setSelections] = React.useState<Record<string, number>>({});
    const [text, setText] = React.useState('');
    const [questionAnswers, setQuestionAnswers] = React.useState<Record<string, string>>({});
    const [optionDetails, setOptionDetails] = React.useState<Record<string, string>>({});
    const [optionDatetime, setOptionDatetime] = React.useState<Record<string, { date_debut: string; heure_debut: string; date_fin: string; heure_fin: string }>>({});
    const [questionDatetime, setQuestionDatetime] = React.useState<Record<string, { date_debut: string; heure_debut: string; date_fin: string; heure_fin: string }>>({});
    const [contactType, setContactType] = React.useState<'PRIVÉ' | 'PROFESSIONNEL'>('PRIVÉ');
    const [contactFirstName, setContactFirstName] = React.useState('');
    const [contactLastName, setContactLastName] = React.useState('');
    const [contactCompanyName, setContactCompanyName] = React.useState('');
    const [contactPhone, setContactPhone] = React.useState('');
    const [attachments, setAttachments] = React.useState<{ name: string; data: string }[]>([]);
    const [submitting, setSubmitting] = React.useState(false);
    const [error, setError] = React.useState('');
    const [paymentLink, setPaymentLink] = React.useState('');

    const [openHelpIds, setOpenHelpIds] = React.useState<Set<string>>(new Set());
    const toggleQuestionHelp = (id: string) => setOpenHelpIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const [rdv, setRdv] = React.useState<ExistingRdv | null>(null);
    const [rdvLocationType, setRdvLocationType] = React.useState<'domicile' | 'entreprise'>('domicile');
    const [rdvAddress, setRdvAddress] = React.useState('');
    const [rdvAddressSelected, setRdvAddressSelected] = React.useState(false);
    const [rdvPostalCode, setRdvPostalCode] = React.useState('');
    const [rdvCity, setRdvCity] = React.useState('');
    const [rdvDate, setRdvDate] = React.useState('');
    const [rdvSlots, setRdvSlots] = React.useState<string[]>([]);
    const [computedRdvDuration, setComputedRdvDuration] = React.useState<number | null>(null);
    const [kmFee, setKmFee] = React.useState<{ fee: number; distance_km: number } | null>(null);
    const [rdvLoadingSlots, setRdvLoadingSlots] = React.useState(false);
    const [selectedSlot, setSelectedSlot] = React.useState<string | null>(null);
    const [rdvBooking, setRdvBooking] = React.useState(false);
    const [rdvError, setRdvError] = React.useState('');

    React.useEffect(() => {
        const root = document.documentElement;
        const previousTheme = root.getAttribute('data-theme');
        const wasDark = root.classList.contains('dark');
        root.setAttribute('data-theme', 'light');
        root.classList.remove('dark');
        root.classList.add('light');
        return () => {
            if (previousTheme) root.setAttribute('data-theme', previousTheme); else root.removeAttribute('data-theme');
            root.classList.toggle('dark', wasDark);
            root.classList.toggle('light', !wasDark);
        };
    }, []);

    React.useEffect(() => {
        if (!token) { setPhase('notfound'); return; }
        // Retour d'un paiement Stripe (cf. GET /documentation/:token/finalize) : si la vérification a
        // échoué côté serveur, la demande n'a jamais été créée — on l'indique ici plutôt que de laisser le
        // client face à un formulaire vide sans explication après avoir cru payer.
        const paymentError = new URLSearchParams(window.location.search).get('error');
        if (paymentError === 'payment' || paymentError === 'payment_incomplete') {
            setError('Le paiement n\'a pas pu être confirmé — votre demande n\'a pas été enregistrée. Merci de réessayer, ou de nous contacter si le montant a bien été débité.');
        } else if (paymentError === 'rdv_conflict') {
            setError('Votre paiement a bien été reçu, mais le créneau demandé n\'est plus disponible — l\'établissement va vous recontacter pour convenir d\'un nouveau rendez-vous.');
        }
        publicDocumentApi.getInfo(token).then((data: any) => {
            if (!data || data.error) { setPhase('notfound'); return; }
            // Démarches enchaînées : cette étape est déjà répondue mais l'envoi en compte d'autres — on
            // renvoie le client là où il s'était arrêté. Le lien du mail pointe toujours la première
            // étape : sans ce rebond, un client interrompu retomberait dessus et ne verrait jamais la suite.
            if (data.chaine?.redirect_token) {
                window.location.replace(`/documentation/${data.chaine.redirect_token}`);
                return;
            }
            setInfo(data);
            setResponded(data.status !== 'pending');
            setPaymentLink(data.stripe_payment_link || '');
            setRdv(data.existing_rdv || null);
            // Adresse déjà connue du contact CRM (Démarche envoyée à un contact existant) — préremplie pour
            // lui faire gagner du temps, avec le même statut "sélectionnée" qu'un choix dans la liste
            // d'autocomplétion (déjà fiable, vient de la fiche client, pas d'une saisie libre).
            if (data.known_address?.address) {
                setRdvAddress(data.known_address.address);
                setRdvPostalCode(data.known_address.postal_code || '');
                setRdvCity(data.known_address.city || '');
                setRdvAddressSelected(true);
            }
            setPhase('ready');

            const brandColor = data.theme_color || '#987b55';
            document.documentElement.style.setProperty('--brand-color', brandColor);
            const hex = brandColor.replace('#', '');
            const r = parseInt(hex.substring(0, 2), 16) || 0;
            const g = parseInt(hex.substring(2, 4), 16) || 0;
            const b = parseInt(hex.substring(4, 6), 16) || 0;
            const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
            document.documentElement.style.setProperty('--brand-fg', luminance > 0.5 ? '#000000' : '#ffffff');
        }).catch(() => setPhase('notfound'));
    }, [token]);

    const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []).slice(0, 5 - attachments.length);
        e.target.value = '';
        files.forEach((file) => {
            const reader = new FileReader();
            reader.onload = () => {
                setAttachments((prev) => prev.length >= 5 ? prev : [...prev, { name: file.name, data: reader.result as string }]);
            };
            reader.readAsDataURL(file);
        });
    };
    const removeAttachment = (index: number) => setAttachments((prev) => prev.filter((_, i) => i !== index));

    const toggleChoice = (choice: Choice) => {
        if (!info) return;
        setSelections((prev) => {
            const isSelected = choice.id in prev;
            if (info.allow_multiple_choices) {
                const next = { ...prev };
                if (isSelected) delete next[choice.id]; else next[choice.id] = 1;
                return next;
            }
            return isSelected ? {} : { [choice.id]: 1 };
        });
    };

    const changeQty = (choiceId: string, delta: number) => {
        setSelections((prev) => {
            const current = prev[choiceId] || 1;
            const next = Math.min(99, Math.max(1, current + delta));
            return { ...prev, [choiceId]: next };
        });
    };

    // Si un rendez-vous existe déjà dès le tout premier chargement (avant toute réponse de notre part),
    // c'est que l'établissement a fixé la date lui-même (RDV pris par téléphone, cf. "Nouveau rendez-vous"
    // côté établissement) — jamais possible dans le parcours normal où le contact choisit sa propre date
    // (celle-ci n'apparaît alors qu'après avoir répondu). Affichage adapté en conséquence ci-dessous.
    const isPrefixedRdv = !responded && !!rdv;

    const selectedChoices = info ? info.choices.filter((c) => c.id in selections) : [];
    const orderTotal = selectedChoices.reduce((sum, c) => sum + c.price_ttc * (selections[c.id] || 1), 0);
    const willRequirePayment = !!info && info.payment_type !== 'none' && (orderTotal > 0 || info.payment_amount > 0);

    // Une option de choix personnalisé configurée avec "bloquer à cette étape" empêche toute suite (message
    // libre, RDV, envoi) dès qu'elle est sélectionnée — seul le message conditionnel de l'option reste visible.
    const isBlocked = info ? info.custom_questions.some((q) => q.type === 'select' &&
        getSelectedLabels(questionAnswers[q.id]).some((label) => q.options.find((o) => o.label === label)?.block)
    ) : false;

    const formatDateFr = (d: string) => { if (!d) return ''; const [y, m, day] = d.split('-'); return `${day}/${m}/${y}`; };

    // Combine la/les option(s) sélectionnée(s) avec le texte de précision saisi (si l'option le propose)
    // pour construire la réponse finale envoyée à l'établissement — ex: "Oui (précisez): un garage", ou
    // "Oui — 20/08/2026 14:00" pour une option "date et heure".
    const buildAnswer = (q: CustomQuestion) => {
        if (q.type === 'datetime') {
            const dt = questionDatetime[q.id];
            if (!dt?.date_debut || !dt?.heure_debut) return '';
            let s = `${formatDateFr(dt.date_debut)} ${dt.heure_debut}`;
            if (dt.date_fin || dt.heure_fin) s += ` → ${dt.date_fin ? formatDateFr(dt.date_fin) : formatDateFr(dt.date_debut)}${dt.heure_fin ? ' ' + dt.heure_fin : ''}`;
            return s;
        }
        if (q.type !== 'select') return (questionAnswers[q.id] || '').trim();
        return getSelectedLabels(questionAnswers[q.id]).map((label) => {
            const opt = q.options.find((o) => o.label === label);
            if (opt?.has_detail_field && opt.detail_field_type === 'datetime') {
                const dt = optionDatetime[opt.id];
                if (!dt?.date_debut || !dt?.heure_debut) return label;
                let s = `${label} — ${formatDateFr(dt.date_debut)} ${dt.heure_debut}`;
                if (dt.date_fin || dt.heure_fin) s += ` → ${dt.date_fin ? formatDateFr(dt.date_fin) : formatDateFr(dt.date_debut)}${dt.heure_fin ? ' ' + dt.heure_fin : ''}`;
                return s;
            }
            if (opt?.has_detail_field) {
                const detail = (optionDetails[opt.id] || '').trim();
                return detail ? `${label} : ${detail}` : label;
            }
            return label;
        }).join(', ');
    };

    // Pour une question "select" dont l'option sélectionnée est de type "date et heure" et bien remplie,
    // extrait les champs structurés à envoyer en plus du texte lisible — alimente la date suggérée du
    // module Commandes (date_debut/heure_debut obligatoires, date_fin/heure_fin seulement si autorisée).
    const buildDatetimePayload = (q: CustomQuestion) => {
        if (q.type === 'datetime') {
            const dt = questionDatetime[q.id];
            if (!dt?.date_debut || !dt?.heure_debut) return null;
            const payload: Record<string, string> = { date_debut: dt.date_debut, heure_debut: dt.heure_debut };
            if (q.allow_end_date !== false) {
                if (dt.date_fin) payload.date_fin = dt.date_fin;
                if (dt.heure_fin) payload.heure_fin = dt.heure_fin;
            }
            return payload;
        }
        if (q.type !== 'select') return null;
        const label = getSelectedLabels(questionAnswers[q.id]).find((l) => {
            const opt = q.options.find((o) => o.label === l);
            return opt?.has_detail_field && opt.detail_field_type === 'datetime';
        });
        if (!label) return null;
        const opt = q.options.find((o) => o.label === label);
        const dt = opt ? optionDatetime[opt.id] : null;
        if (!opt || !dt?.date_debut || !dt?.heure_debut) return null;
        const payload: Record<string, string> = { option_id: opt.id, date_debut: dt.date_debut, heure_debut: dt.heure_debut };
        if (opt.allow_end_date !== false) {
            if (dt.date_fin) payload.date_fin = dt.date_fin;
            if (dt.heure_fin) payload.heure_fin = dt.heure_fin;
        }
        return payload;
    };

    const loadRdvSlots = async (date: string) => {
        if (!token) return;
        setRdvDate(date);
        setRdvSlots([]);
        setSelectedSlot(null);
        setRdvError('');
        if (!date) return;
        setRdvLoadingSlots(true);
        try {
            const choicesPayload = Object.entries(selections).map(([id, qty]) => ({ id, qty }));
            const res: any = await publicDocumentApi.getRdvAvailability(token, date, choicesPayload);
            setRdvSlots(Array.isArray(res?.slots) ? res.slots : []);
            if (res?.duration_minutes) setComputedRdvDuration(res.duration_minutes);
        } catch (e) {
            setRdvSlots([]);
        } finally {
            setRdvLoadingSlots(false);
        }
    };

    // Si le client change ses choix après avoir déjà sélectionné une date (la durée du RDV peut dépendre des
    // choix, cf. loadRdvSlots), les créneaux affichés doivent être recalculés — sinon un créneau resterait
    // proposé alors qu'il ne convient plus à la nouvelle durée.
    React.useEffect(() => {
        if (rdvDate) loadRdvSlots(rdvDate);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [JSON.stringify(selections)]);

    // La "raison" du RDV est dérivée automatiquement des choix déjà sélectionnés par le client (pas
    // redemandée) — repli sur le nom de la démarche si aucun choix n'est configuré/sélectionné.
    // Une réponse cochée peut ouvrir une autre Démarche (réglé sur la réponse elle-même côté
    // établissement). Le bouton du bas doit alors annoncer « Suivant » et non « Envoyer ma demande » : la
    // page connaît les réponses cochées, elle n'a besoin de personne pour le savoir.
    const ouvreUneSuite = React.useMemo(() => (info?.custom_questions || []).some((q) => {
        const choisies = getSelectedLabels(questionAnswers[q.id]);
        return (q.options || []).some((o) => !!o.demarche_suivante_id && choisies.includes(o.label));
    }), [info, questionAnswers]);

    const rdvReason = () => (info && selectedChoices.length > 0
        ? selectedChoices.map((c) => `${c.label}${(selections[c.id] || 1) > 1 ? ` x${selections[c.id]}` : ''}`).join(', ')
        : info?.name || '');

    // Vrai si le RDV aura bien lieu au domicile du client — soit parce que l'établissement n'intervient qu'à
    // domicile, soit parce que le client l'a choisi (mode "les_deux") — cas où son adresse est nécessaire à
    // la fois pour que le salarié sache où se rendre et pour calculer les frais kilométriques à la clôture.
    const isDomicileRdv = info?.location_mode === 'les_deux' ? rdvLocationType === 'domicile' : info?.location_mode === 'domicile';

    // Estimation des frais kilométriques affichée en direct pendant la saisie de l'adresse — pour que le
    // client voie le vrai total (y compris les frais) avant de payer, pas seulement une fois sur Stripe/Revolut.
    React.useEffect(() => {
        if (!token || !isDomicileRdv || !rdvAddress.trim()) { setKmFee(null); return; }
        const timer = setTimeout(() => {
            publicDocumentApi.getKmFeeEstimate(token, rdvAddress.trim(), rdvPostalCode.trim(), rdvCity.trim())
                .then((data: any) => setKmFee(data?.fee > 0 ? data : null))
                .catch(() => setKmFee(null));
        }, 600);
        return () => clearTimeout(timer);
    }, [token, isDomicileRdv, rdvAddress, rdvPostalCode, rdvCity]);

    // Réserve le créneau déjà sélectionné (staging) — n'est jamais déclenché par le simple clic sur un
    // créneau, seulement par un envoi explicite (bouton principal ou bouton dédié pour une visite ultérieure).
    const bookSelectedSlot = async (): Promise<any> => {
        if (!token || !rdvDate || !selectedSlot) return null;
        if (isDomicileRdv && !rdvAddress.trim()) { setRdvError('Merci d\'indiquer votre adresse pour l\'intervention à domicile.'); return { error: 'address' }; }
        if (isDomicileRdv && !rdvAddressSelected) { setRdvError('Merci de sélectionner votre adresse dans la liste proposée.'); return { error: 'address' }; }
        const res: any = await publicDocumentApi.bookRdv(token, { date: rdvDate, start_time: selectedSlot, subject: rdvReason(), notes: text.trim(), location_type: rdvLocationType, address: isDomicileRdv ? rdvAddress.trim() : undefined, postal_code: isDomicileRdv ? rdvPostalCode.trim() : undefined, city: isDomicileRdv ? rdvCity.trim() : undefined });
        if (res?.error) { setRdvError(res.error); return res; }
        setRdv({ date: res.date, start_time: res.start_time, end_time: res.end_time, status: 'en_attente' });
        return res;
    };

    const handleSubmit = async () => {
        if (!token || !info) return;
        // Le texte libre n'est jamais obligatoire pour envoyer sa demande (utile quand la documentation
        // propose surtout une prise de rendez-vous) — seul un choix reste requis si le modèle en configure.
        if (info.response_type === 'choice' && selectedChoices.length === 0) { setError('Merci de faire un choix.'); return; }
        const missingRequired = info.custom_questions.find((q) => {
            if (!q.required) return false;
            if (q.type === 'datetime') { const dt = questionDatetime[q.id]; return !dt?.date_debut || !dt?.heure_debut; }
            return !(questionAnswers[q.id] || '').trim();
        });
        if (missingRequired) { setError(`Merci de répondre à : ${missingRequired.question}`); return; }
        // Une option "date et heure" sélectionnée impose toujours sa date + heure de début, même quand la
        // question elle-même n'est pas marquée obligatoire (comme demandé pour ce type de champ).
        const missingDatetime = info.custom_questions.find((q) => {
            if (q.type !== 'select') return false;
            const label = getSelectedLabels(questionAnswers[q.id]).find((l) => {
                const opt = q.options.find((o) => o.label === l);
                return opt?.has_detail_field && opt.detail_field_type === 'datetime';
            });
            if (!label) return false;
            const opt = q.options.find((o) => o.label === label);
            const dt = opt ? optionDatetime[opt.id] : null;
            return !dt?.date_debut || !dt?.heure_debut;
        });
        if (missingDatetime) { setError(`Merci de préciser la date et l'heure pour : ${missingDatetime.question}`); return; }
        // Une question de type "Date et heure" partiellement remplie (un seul des deux champs de début) impose
        // aussi de compléter, même si la question n'est pas obligatoire — même logique que pour une option.
        const missingQuestionDatetime = info.custom_questions.find((q) => {
            if (q.type !== 'datetime') return false;
            const dt = questionDatetime[q.id];
            const hasAny = !!(dt?.date_debut || dt?.heure_debut);
            return hasAny && (!dt?.date_debut || !dt?.heure_debut);
        });
        if (missingQuestionDatetime) { setError(`Merci de préciser la date et l'heure pour : ${missingQuestionDatetime.question}`); return; }
        if (selectedSlot && isDomicileRdv && !rdvAddress.trim()) { setError('Merci d\'indiquer votre adresse pour l\'intervention à domicile.'); return; }
        if (selectedSlot && isDomicileRdv && !rdvAddressSelected) { setError('Merci de sélectionner votre adresse dans la liste proposée.'); return; }
        // Contact CRM incomplet (lien envoyé par simple email, sans fiche CRM préexistante) — ces
        // informations sont requises pour que l'établissement puisse vous recontacter et suivre la demande.
        if (info.needs_contact_info) {
            if (!contactPhone.trim()) { setError('Merci de renseigner votre téléphone.'); return; }
            if (contactType === 'PROFESSIONNEL' && !contactCompanyName.trim()) { setError('Merci de renseigner le nom de votre société.'); return; }
            if (contactType === 'PRIVÉ' && !contactLastName.trim()) { setError('Merci de renseigner votre nom.'); return; }
        }
        setSubmitting(true);
        setError('');
        setRdvError('');
        try {
            const choicesPayload = selectedChoices.map((c) => ({ id: c.id, qty: selections[c.id] || 1 }));
            const answersPayload = info.custom_questions.map((q) => ({ id: q.id, answer: buildAnswer(q), ...(buildDatetimePayload(q) || {}) })).filter((a) => a.answer.trim());
            const contactInfoPayload = info.needs_contact_info
                ? { type: contactType, first_name: contactFirstName.trim(), last_name: contactLastName.trim(), company_name: contactCompanyName.trim(), phone: contactPhone.trim() }
                : undefined;
            const rdvPayload = selectedSlot
                ? { date: rdvDate, start_time: selectedSlot, subject: rdvReason(), notes: text.trim(), location_type: rdvLocationType, address: isDomicileRdv ? rdvAddress.trim() : undefined, postal_code: isDomicileRdv ? rdvPostalCode.trim() : undefined, city: isDomicileRdv ? rdvCity.trim() : undefined }
                : undefined;
            const res: any = await publicDocumentApi.respond(token, { choices: choicesPayload, text: text.trim(), custom_answers: answersPayload, contact_info: contactInfoPayload, attachments, rdv: rdvPayload });
            if (res?.error) { setError(res.error); return; }
            // Paiement requis : rien n'est encore enregistré côté serveur (démarche et RDV compris) tant que
            // Stripe ne confirme pas le paiement — la réservation du créneau et la création de la démarche
            // n'arrivent qu'au retour de paiement (GET .../finalize), jamais ici.
            if (res.pending_payment) {
                window.location.href = res.stripe_payment_link;
                return;
            }
            setResponded(true);
            if (selectedSlot) await bookSelectedSlot();
            // Étape suivante de l'enchaînement : la réponse qui vient d'être envoyée est enregistrée
            // définitivement, le client passe à la suivante sans repasser par sa boîte mail. Rechargement
            // complet volontaire — chaque étape repart d'un formulaire vierge.
            if (res.next_token) {
                window.location.href = `/documentation/${res.next_token}`;
                return;
            }
        } catch (e: any) {
            setError(e?.message || 'Erreur lors de l\'envoi. Merci de réessayer.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleBookRdvOnly = async () => {
        setRdvBooking(true);
        try {
            await bookSelectedSlot();
        } catch (e: any) {
            setRdvError(e?.message || 'Erreur lors de la réservation.');
        } finally {
            setRdvBooking(false);
        }
    };

    if (phase === 'loading') {
        return <div className="min-h-screen flex items-center justify-center bg-surface-app"><Loader2 size={32} className="animate-spin text-content-muted" /></div>;
    }

    if (phase === 'notfound' || !info) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-surface-app p-4">
                <div className="text-center max-w-sm">
                    <FileText size={32} className="mx-auto mb-3 text-content-muted opacity-40" />
                    <h1 className="text-base font-bold text-content mb-1.5">Lien invalide</h1>
                    <p className="text-content-muted text-xs">Ce lien n'est plus valide ou a déjà été utilisé.</p>
                </div>
                <PoweredByFooter />
            </div>
        );
    }

    const minDate = new Date().toISOString().split('T')[0];

    return (
        <div className="min-h-screen bg-surface-app py-8 px-4">
            <div className="max-w-md mx-auto space-y-5">
                <header className="text-center space-y-1">
                    {info.logo_url && <img src={info.logo_url} alt={info.company_name} className="h-10 mx-auto object-contain" />}
                    <h1 className="text-lg font-black text-content">{info.company_name}</h1>
                    {/* Le nom du modèle (« Gâteau Angelina - PRIVATISATION ») est un repère INTERNE : il sert
                        à s'y retrouver dans la liste des Démarches, le client n'a pas à le lire. Demande
                        d'Emeni (2026-08-30). Il reste utilisé comme motif du rendez-vous, que seul
                        l'établissement voit dans RDV Pro. */}
                    {info.chaine && info.chaine.total > 1 && (
                        <p className="text-[11px] font-bold text-brand-600">Étape {info.chaine.position} sur {info.chaine.total}</p>
                    )}
                </header>

                {error && (
                    <p className="text-xs text-red-500 font-semibold bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-center">{error}</p>
                )}

                {info.instructions_text && (
                    <div className="bg-surface border border-border rounded-2xl p-4">
                        <p className="text-sm text-content whitespace-pre-wrap">{info.instructions_text}</p>
                    </div>
                )}

                {!responded && info.needs_contact_info && (
                    <div className="bg-surface border border-border rounded-2xl p-4 space-y-3">
                        <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Vos informations</label>
                        <div className="grid grid-cols-2 gap-2">
                            {(['PRIVÉ', 'PROFESSIONNEL'] as const).map((t) => (
                                <button key={t} type="button" onClick={() => setContactType(t)}
                                    className={`px-3 py-2.5 rounded-xl text-xs font-bold border transition-all ${contactType === t ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                    {t === 'PRIVÉ' ? 'Particulier' : 'Professionnel'}
                                </button>
                            ))}
                        </div>
                        {contactType === 'PROFESSIONNEL' ? (
                            <input type="text" value={contactCompanyName} onChange={(e) => setContactCompanyName(e.target.value)}
                                placeholder="Nom de la société" className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                        ) : (
                            <div className="grid grid-cols-2 gap-2">
                                <input type="text" value={contactFirstName} onChange={(e) => setContactFirstName(e.target.value)}
                                    placeholder="Prénom" className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                                <input type="text" value={contactLastName} onChange={(e) => setContactLastName(e.target.value)}
                                    placeholder="Nom" className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                            </div>
                        )}
                        <input type="tel" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)}
                            placeholder="Téléphone" className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                    </div>
                )}

                <div className="bg-surface border border-border rounded-2xl p-4 space-y-4">
                    {info.has_attachment && (
                        <a href={`/api/documentation/${token}/attachment`} target="_blank" rel="noreferrer"
                            className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-border text-content-muted hover:text-content hover:bg-surface-soft text-xs font-bold transition-all">
                            <Download size={14} /> {info.attachment_name || 'Télécharger le document'}
                        </a>
                    )}

                    {!responded ? (
                        <>
                            {(info.response_type === 'choice' || info.response_type === 'both') && (
                                <div className="space-y-1.5">
                                    <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">
                                        Votre choix {info.allow_multiple_choices ? '(plusieurs possibles)' : ''}
                                    </label>
                                    {info.choices.map((choice) => {
                                        const isSelected = choice.id in selections;
                                        return (
                                            <div key={choice.id} className={`w-full rounded-xl border transition-all ${isSelected ? 'bg-brand-500/10 border-brand-500' : 'border-border'}`}>
                                                <button type="button" onClick={() => toggleChoice(choice)}
                                                    className={`w-full flex items-center justify-between gap-2 text-left px-3 py-2.5 text-sm font-bold ${isSelected ? 'text-brand-600' : 'text-content hover:bg-surface-soft'}`}>
                                                    <span>{choice.label}</span>
                                                    {choice.price_ttc > 0 && <span className="text-xs font-normal shrink-0">{formatPrice(choice.price_ttc)}</span>}
                                                </button>
                                                {isSelected && choice.price_ttc > 0 && choice.has_quantity !== false && (
                                                    <div className="flex items-center gap-3 px-3 pb-2.5">
                                                        <span className="text-[11px] text-content-muted">Quantité</span>
                                                        <div className="flex items-center gap-2">
                                                            <button type="button" onClick={() => changeQty(choice.id, -1)} className="w-6 h-6 flex items-center justify-center rounded-lg bg-surface border border-border text-content-muted hover:text-content"><Minus size={12} /></button>
                                                            <span className="text-sm font-bold text-content w-4 text-center">{selections[choice.id] || 1}</span>
                                                            <button type="button" onClick={() => changeQty(choice.id, 1)} className="w-6 h-6 flex items-center justify-center rounded-lg bg-surface border border-border text-content-muted hover:text-content"><Plus size={12} /></button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {kmFee && kmFee.fee > 0 && (
                                        <p className="text-right text-xs text-content-muted">Frais kilométriques ({kmFee.distance_km} km aller-retour) : {formatPrice(kmFee.fee)}</p>
                                    )}
                                    {orderTotal > 0 && (
                                        <p className="text-right text-sm font-bold text-content pt-1">Total : {formatPrice(orderTotal + (kmFee?.fee || 0))}</p>
                                    )}
                                </div>
                            )}

                            {info.custom_questions.length > 0 && (
                                <div className="space-y-3">
                                    {info.custom_questions.map((q) => {
                                        const selectedLabels = getSelectedLabels(questionAnswers[q.id]);
                                        const toggleOption = (label: string) => setQuestionAnswers((prev) => {
                                            const current = getSelectedLabels(prev[q.id]);
                                            let next: string[];
                                            if (q.allow_multiple) next = current.includes(label) ? current.filter((l) => l !== label) : [...current, label];
                                            else next = current.includes(label) ? [] : [label];
                                            return { ...prev, [q.id]: next.join(', ') };
                                        });
                                        return (
                                            <div key={q.id} className="space-y-1.5">
                                                <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest flex items-center gap-1.5">
                                                    {q.question}{q.required && <span className="text-red-500"> *</span>}
                                                    {(q.help_text || q.help_image) && (
                                                        <button type="button" onClick={() => toggleQuestionHelp(q.id)}
                                                            className="w-4 h-4 rounded-full bg-surface-soft border border-border text-content-muted hover:text-brand-600 hover:border-brand-500 flex items-center justify-center normal-case tracking-normal shrink-0" title="Aide">
                                                            <HelpCircle size={11} />
                                                        </button>
                                                    )}
                                                </label>
                                                {(q.help_text || q.help_image) && openHelpIds.has(q.id) && (
                                                    <div className="bg-surface-soft border border-border rounded-xl p-3 space-y-2">
                                                        {q.help_image && <img src={q.help_image} alt="Aide" className="max-h-48 w-auto rounded-lg mx-auto" />}
                                                        {q.help_text && <p className="text-xs text-content whitespace-pre-wrap">{q.help_text}</p>}
                                                    </div>
                                                )}
                                                {q.type === 'select' ? (
                                                    <>
                                                        <div className="grid grid-cols-2 gap-2">
                                                            {q.options.map((o) => (
                                                                <button key={o.id} type="button" onClick={() => toggleOption(o.label)}
                                                                    className={`px-3 py-2.5 rounded-xl text-xs font-bold border transition-all ${selectedLabels.includes(o.label) ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                                                    {o.label}
                                                                </button>
                                                            ))}
                                                        </div>
                                                        {q.options.filter((o) => selectedLabels.includes(o.label) && o.message).map((o) => (
                                                            <p key={o.id} className="text-xs text-content bg-surface-soft rounded-xl p-3 border border-border">{o.message}</p>
                                                        ))}
                                                        {q.options.filter((o) => selectedLabels.includes(o.label) && o.has_detail_field && o.detail_field_type !== 'datetime').map((o) => (
                                                            <input key={o.id} type="text" value={optionDetails[o.id] || ''}
                                                                onChange={(e) => setOptionDetails((prev) => ({ ...prev, [o.id]: e.target.value }))}
                                                                placeholder="Précisez..." className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                                                        ))}
                                                        {q.options.filter((o) => selectedLabels.includes(o.label) && o.has_detail_field && o.detail_field_type === 'datetime').map((o) => {
                                                            const dt = optionDatetime[o.id] || { date_debut: '', heure_debut: '', date_fin: '', heure_fin: '' };
                                                            const setDt = (patch: Partial<typeof dt>) => setOptionDatetime((prev) => ({ ...prev, [o.id]: { ...dt, ...patch } }));
                                                            return (
                                                                <div key={o.id} className="grid grid-cols-2 gap-2 bg-surface-soft border border-border rounded-xl p-3">
                                                                    <div className="space-y-1 col-span-2 sm:col-span-1">
                                                                        <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Date *</label>
                                                                        <input type="date" value={dt.date_debut} onChange={(e) => setDt({ date_debut: e.target.value })}
                                                                            className="w-full bg-surface border border-border rounded-lg px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                                                    </div>
                                                                    <div className="space-y-1 col-span-2 sm:col-span-1">
                                                                        <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Heure *</label>
                                                                        <input type="time" value={dt.heure_debut} onChange={(e) => setDt({ heure_debut: e.target.value })}
                                                                            className="w-full bg-surface border border-border rounded-lg px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                                                    </div>
                                                                    {o.allow_end_date !== false && (
                                                                        <>
                                                                            <div className="space-y-1 col-span-2 sm:col-span-1">
                                                                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Date de fin</label>
                                                                                <input type="date" value={dt.date_fin} onChange={(e) => setDt({ date_fin: e.target.value })}
                                                                                    className="w-full bg-surface border border-border rounded-lg px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                                                            </div>
                                                                            <div className="space-y-1 col-span-2 sm:col-span-1">
                                                                                <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Heure de fin</label>
                                                                                <input type="time" value={dt.heure_fin} onChange={(e) => setDt({ heure_fin: e.target.value })}
                                                                                    className="w-full bg-surface border border-border rounded-lg px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                                                            </div>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </>
                                ) : q.type === 'datetime' ? (
                                    (() => {
                                        const dt = questionDatetime[q.id] || { date_debut: '', heure_debut: '', date_fin: '', heure_fin: '' };
                                        const setDt = (patch: Partial<typeof dt>) => setQuestionDatetime((prev) => ({ ...prev, [q.id]: { ...dt, ...patch } }));
                                        return (
                                            <div className="grid grid-cols-2 gap-2 bg-surface-soft border border-border rounded-xl p-3">
                                                <div className="space-y-1 col-span-2 sm:col-span-1">
                                                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Date *</label>
                                                    <input type="date" value={dt.date_debut} onChange={(e) => setDt({ date_debut: e.target.value })}
                                                        className="w-full bg-surface border border-border rounded-lg px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                                </div>
                                                <div className="space-y-1 col-span-2 sm:col-span-1">
                                                    <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Heure *</label>
                                                    <input type="time" value={dt.heure_debut} onChange={(e) => setDt({ heure_debut: e.target.value })}
                                                        className="w-full bg-surface border border-border rounded-lg px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                                </div>
                                                {q.allow_end_date !== false && (
                                                    <>
                                                        <div className="space-y-1 col-span-2 sm:col-span-1">
                                                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Date de fin</label>
                                                            <input type="date" value={dt.date_fin} onChange={(e) => setDt({ date_fin: e.target.value })}
                                                                className="w-full bg-surface border border-border rounded-lg px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                                        </div>
                                                        <div className="space-y-1 col-span-2 sm:col-span-1">
                                                            <label className="text-[10px] font-bold text-content-muted uppercase tracking-widest">Heure de fin</label>
                                                            <input type="time" value={dt.heure_fin} onChange={(e) => setDt({ heure_fin: e.target.value })}
                                                                className="w-full bg-surface border border-border rounded-lg px-2.5 py-2 text-sm text-content outline-none focus:border-brand-500" />
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        );
                                    })()
                                ) : q.type === 'deroule' ? (
                                    /* Le déroulé s'enregistre dans le MÊME champ texte que les autres réponses, une
                                       ligne par moment : il traverse donc l'email, le récapitulatif et le PDF sans
                                       qu'aucun d'eux ait à connaître ce nouveau type. */
                                    <div className="bg-surface-soft border border-border rounded-xl p-3">
                                        <DerouleEditor valeur={questionAnswers[q.id] || ''}
                                            onChange={(t) => setQuestionAnswers((prev) => ({ ...prev, [q.id]: t }))} />
                                    </div>
                                ) : (
                                    <input type="text" value={questionAnswers[q.id] || ''} onChange={(e) => setQuestionAnswers((prev) => ({ ...prev, [q.id]: e.target.value }))}
                                        className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {!isBlocked && (info.response_type === 'question' || info.response_type === 'both') && (
                                <div className="space-y-1.5">
                                    <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Message (optionnel)</label>
                                    <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3}
                                        className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500 resize-none" />
                                </div>
                            )}

                            {!isBlocked && info.allow_document_upload && (
                                <div className="space-y-1.5">
                                    <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Documents à joindre (optionnel)</label>
                                    {attachments.map((f, i) => (
                                        <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 rounded-xl bg-surface-soft border border-border">
                                            <span className="text-xs text-content truncate flex items-center gap-1.5"><Paperclip size={12} className="shrink-0" /> {f.name}</span>
                                            <button type="button" onClick={() => removeAttachment(i)} className="text-content-muted hover:text-red-400 shrink-0"><X size={13} /></button>
                                        </div>
                                    ))}
                                    {attachments.length < 5 && (
                                        <label className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-surface-soft border border-border text-sm text-content-muted hover:text-content cursor-pointer transition-colors">
                                            <Paperclip size={14} /> Ajouter un fichier
                                            <input type="file" accept="image/*,.pdf,.xls,.xlsx,.csv" multiple className="hidden" onChange={handleFilesSelected} />
                                        </label>
                                    )}
                                </div>
                            )}

                        </>
                    ) : (
                        <div className="text-center space-y-3 py-2">
                            <CheckCircle2 size={28} className="mx-auto text-emerald-500" />
                            <p className="text-sm font-bold text-content">Merci, votre demande a bien été envoyée.</p>
                            {paymentLink && (
                                <a href={paymentLink} target="_blank" rel="noreferrer"
                                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg font-bold text-sm transition-all">
                                    <ExternalLink size={14} /> Procéder au paiement
                                </a>
                            )}
                        </div>
                    )}
                </div>

                {info.offer_appointment && !isBlocked && (
                    <div className="bg-surface border border-border rounded-2xl p-4 space-y-3">
                        <div>
                            <h2 className="text-sm font-bold text-content flex items-center gap-2"><CalendarClock size={16} className="text-brand-500" /> {isPrefixedRdv ? 'Votre rendez-vous' : 'Prendre rendez-vous'}</h2>
                            {!rdv && (computedRdvDuration ?? info.rdv_duration_minutes) && (
                                <p className="text-[11px] text-content-muted mt-0.5">Durée prévue : {computedRdvDuration ?? info.rdv_duration_minutes} minutes</p>
                            )}
                        </div>
                        {rdv ? (
                            <p className="text-sm text-content bg-surface-soft rounded-xl p-3 border border-border">
                                {isPrefixedRdv && rdv.status === 'en_attente' && (
                                    <>Rendez-vous prévu le <strong>{new Date(rdv.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</strong> à <strong>{rdv.start_time}</strong> — merci de compléter les informations ci-dessus pour le confirmer.</>
                                )}
                                {rdv.status === 'confirme' && (
                                    <>Rendez-vous confirmé le <strong>{new Date(rdv.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</strong> à <strong>{rdv.start_time}</strong>.</>
                                )}
                                {!isPrefixedRdv && rdv.status === 'en_attente' && (
                                    <>Votre demande de rendez-vous pour le <strong>{new Date(rdv.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</strong> à <strong>{rdv.start_time}</strong> a bien été envoyée — en attente de confirmation par l'établissement.</>
                                )}
                                {rdv.status === 'refuse' && (
                                    <>Votre demande de rendez-vous pour le {new Date(rdv.date + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })} à {rdv.start_time} n'a malheureusement pas pu être acceptée.{rdv.refuse_reason ? <> Motif : <strong>{rdv.refuse_reason}</strong>.</> : ''}</>
                                )}
                            </p>
                        ) : (
                            <>
                                {info.location_mode === 'les_deux' && (
                                    <div className="space-y-1.5">
                                        <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Où souhaitez-vous être reçu(e) ?</label>
                                        <div className="grid grid-cols-2 gap-2">
                                            {(['domicile', 'entreprise'] as const).map((t) => (
                                                <button key={t} type="button" onClick={() => setRdvLocationType(t)}
                                                    className={`px-3 py-2.5 rounded-xl text-xs font-bold border transition-all ${rdvLocationType === t ? 'bg-brand-600 border-brand-600 text-brand-fg' : 'border-border text-content-muted hover:text-content'}`}>
                                                    {t === 'domicile' ? 'À mon adresse' : "Dans l'établissement"}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {isDomicileRdv && (
                                    <div className="space-y-2">
                                        <div className="space-y-1.5">
                                            <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Adresse</label>
                                            <AddressAutocompleteInput value={rdvAddress} onChange={(v) => { setRdvAddress(v); setRdvAddressSelected(false); }}
                                                onSelect={(s) => { setRdvPostalCode(s.postcode); setRdvCity(s.city); setRdvAddressSelected(true); }}
                                                placeholder="Numéro et rue"
                                                className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                                            {rdvAddress.trim() && !rdvAddressSelected && (
                                                <p className="text-[11px] text-amber-500 font-semibold">Merci de sélectionner votre adresse dans la liste proposée.</p>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            <div className="space-y-1.5">
                                                <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Code postal</label>
                                                <input value={rdvPostalCode} onChange={(e) => setRdvPostalCode(e.target.value)}
                                                    className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                                            </div>
                                            <div className="space-y-1.5">
                                                <label className="text-[11px] font-bold text-content-muted uppercase tracking-widest">Ville</label>
                                                <input value={rdvCity} onChange={(e) => setRdvCity(e.target.value)}
                                                    className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500" />
                                            </div>
                                        </div>
                                    </div>
                                )}
                                <input type="date" min={minDate} value={rdvDate} onChange={(e) => loadRdvSlots(e.target.value)} className={`w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-sm text-content outline-none focus:border-brand-500`} />
                                {rdvLoadingSlots && <div className="flex justify-center py-3"><Loader2 size={16} className="animate-spin text-content-muted" /></div>}
                                {!rdvLoadingSlots && rdvDate && rdvSlots.length === 0 && <p className="text-xs text-content-muted">Aucun créneau disponible ce jour-là.</p>}
                                {rdvSlots.length > 0 && (
                                    <div className="grid grid-cols-4 gap-2">
                                        {rdvSlots.map((slot) => (
                                            <button key={slot} type="button" onClick={() => setSelectedSlot(slot)}
                                                className={`px-2 py-2 rounded-lg border text-xs font-bold transition-all ${selectedSlot === slot ? 'bg-brand-500/10 border-brand-500 text-brand-600' : 'border-border text-content hover:bg-brand-500/10 hover:border-brand-500 hover:text-brand-600'}`}>
                                                {slot}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {selectedSlot && responded && (
                                    <button onClick={handleBookRdvOnly} disabled={rdvBooking}
                                        className="w-full px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50 transition-all">
                                        {rdvBooking ? 'Envoi...' : 'Demander ce rendez-vous'}
                                    </button>
                                )}
                                {rdvError && <p className="text-xs text-red-500 font-semibold">{rdvError}</p>}
                            </>
                        )}
                    </div>
                )}

                {!responded && !isBlocked && (
                    <div className="bg-surface border border-border rounded-2xl p-4 space-y-3">
                        {info.payment_type !== 'none' && (orderTotal > 0 || info.payment_amount > 0) && (
                            <p className="text-xs text-content-muted bg-surface-soft rounded-xl p-3 border border-border">
                                {info.payment_type === 'deposit'
                                    ? `Un acompte de ${info.deposit_percent}% du total`
                                    : 'Le montant total'} vous sera demandé après votre réponse.
                            </p>
                        )}
                        <button onClick={handleSubmit} disabled={submitting}
                            className="w-full px-4 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50 transition-all">
                            {submitting ? 'Envoi...' : willRequirePayment ? 'Passer au paiement' : (info.chaine?.has_next || ouvreUneSuite) ? 'Suivant' : 'Envoyer ma demande'}
                        </button>
                    </div>
                )}

                <div className="text-center"><PoweredByFooter /></div>
            </div>
        </div>
    );
};
